import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, CardBody, Col, Row, Form, Label, Input, Spinner } from 'reactstrap';
import { toast } from 'react-toastify';
import { get, putJson } from '../../../hook/request';
import { Btn, H3 } from '../../../AbstractElements';
import NotesSection from './NotesSection';

type ApiAnagrafica = {
  consumer_cod_id: string;
  consumer_id: string;
  cognome: string;
  nome: string;
  indirizzo: string | null;
  citta: string | null;
  pr: string | null;
  nazione: string | null;
  cap: string | null;
  telefono: string | null;
  telefono2: string | null;
  eta: number | null;
  note: string | null;
  mz: string | null;            // "Si" | "No" | null
  fidelity: string | number | null; // "No" | "Si" | 0 | 1 | null
  data_creaz_negozio: string | null;   // ISO o null
  data_creaz_assoluto: string | null;  // ISO o null
  negozio: string | null;
  email: string | null;
  titolo: string | null;
  nc: number | string | null;
  promozione: string | null;
  data_nascita: string | null;     // ISO o null
  ult_acq_negozio: string | null;
  ult_acq_assoluto: string | null;
  pref_contatto: string | null;
  professione: string | null;
  interessi: string | null;
  building: string | null;
  apt_number: string | null;
};

type FormState = {
  consumer_cod_id: string;
  consumer_id: string;
  cognome: string;
  nome: string;
  negozio: string;
  eta: string;              // mantengo string per input number “morbido”
  data_nascita: string;     // input type="date" => yyyy-mm-dd | ''
  telefono: string;
  telefono2: string;
  email: string;
  titolo: string;

  // indirizzo
  indirizzo: string;
  building: string;
  citta: string;
  pr: string;
  nazione: string;
  cap: string;
  apt_number: string;

  // altro
  fidelity: 0 | 1;
  nc: string;
  promozione: string;
  mz: 'Si' | 'No';
  data_creaz_negozio: string;
  data_creaz_assoluto: string;
  ult_acq_negozio: string;
  ult_acq_assoluto: string;
  pref_contatto: string;
  professione: string;
  interessi: string;
  note: string;
};

function toDateInputValue(isoOrNull: string | null): string {
  if (!isoOrNull) return '';
  // accetta sia "YYYY-MM-DD" che "YYYY-MM-DD HH:mm:ss"
  const s = isoOrNull.trim();
  if (s.length >= 10) return s.slice(0, 10);
  return '';
}

function truthyFidelity(v: ApiAnagrafica['fidelity']): 0 | 1 {
  if (v === 1 || v === '1' || v === 'Si' || v === 'si' || v === 'SI') return 1;
  return 0;
}

export default function AnagraficaEdit() {
  const { slug } = useParams<{ slug: string }>();
  const id = useMemo(() => String(slug || ''), [slug]);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);

  const [form, setForm] = useState<FormState>({
    consumer_cod_id: '',
    consumer_id: '',
    cognome: '',
    nome: '',
    negozio: '',
    eta: '',
    data_nascita: '',
    telefono: '',
    telefono2: '',
    email: '',
    titolo: '',

    indirizzo: '',
    building: '',
    citta: '',
    pr: '',
    nazione: '',
    cap: '',
    apt_number: '',

    fidelity: 0,
    nc: '',
    promozione: '',
    mz: 'No',
    data_creaz_negozio: '',
    data_creaz_assoluto: '',
    ult_acq_negozio: '',
    ult_acq_assoluto: '',
    pref_contatto: '',
    professione: '',
    interessi: '',
    note: '',
  });

  // refs per le 4 card
  const profiloRef = useRef<HTMLDivElement | null>(null);
  const indirizzoRef = useRef<HTMLDivElement | null>(null);
  const negozioRef = useRef<HTMLDivElement | null>(null);
  const marketingRef = useRef<HTMLDivElement | null>(null);
  const noteRef = useRef<HTMLDivElement | null>(null);

  const scrollToSection = (ref: React.RefObject<HTMLDivElement | null>) => {
    ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };


  // LOAD
  useEffect(() => {
    (async () => {
      if (!id) return;
      try {
        setLoading(true);
        const res = await get<any>('/anagrafica/edit', { ID: id });
        const payload = (res as any)?.data ?? res;
        const dataInner =
          Array.isArray(payload?.data) ? payload.data[0] :
            (payload?.data ?? payload);

        if (!dataInner) {
          toast.warn('Nessun dato trovato per questo ID');
          setLoading(false);
          return;
        }

        setForm({
          consumer_cod_id: dataInner.consumer_cod_id ?? '',
          consumer_id: dataInner.consumer_id ?? '',
          cognome: dataInner.cognome ?? '',
          nome: dataInner.nome ?? '',
          negozio: dataInner.negozio ?? '',
          eta: dataInner.eta != null ? String(dataInner.eta) : '',
          data_nascita: toDateInputValue(dataInner.data_nascita),

          telefono: dataInner.telefono ?? '',
          telefono2: dataInner.telefono2 ?? '',
          email: dataInner.email ?? '',
          titolo: dataInner.titolo ?? '',

          indirizzo: dataInner.indirizzo ?? '',
          building: dataInner.building ?? '',
          citta: dataInner.citta ?? '',
          pr: dataInner.pr ?? '',
          nazione: dataInner.nazione ?? '',
          cap: dataInner.cap ?? '',
          apt_number: dataInner.apt_number ?? '',

          fidelity: truthyFidelity(dataInner.fidelity),
          nc: dataInner.nc != null ? String(dataInner.nc) : '',
          promozione: dataInner.promozione ?? '',
          mz: (dataInner.mz === 'Si' || dataInner.mz === 'SI') ? 'Si' : 'No',
          data_creaz_negozio: toDateInputValue(dataInner.data_creaz_negozio),
          data_creaz_assoluto: toDateInputValue(dataInner.data_creaz_assoluto),
          ult_acq_negozio: toDateInputValue(dataInner.ult_acq_negozio),
          ult_acq_assoluto: toDateInputValue(dataInner.ult_acq_assoluto),
          pref_contatto: dataInner.pref_contatto ?? '',
          professione: dataInner.professione ?? '',
          interessi: dataInner.interessi ?? '',
          note: dataInner.note ?? '',
        });
      } catch (e: any) {
        const msg = e?.response?.data?.message ?? e?.message ?? 'Errore nel caricamento';
        toast.error(msg);
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);


  // HANDLERS
  const onChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: type === 'number' ? String(value) : value,
    }));
  };

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!id) return;

    const body = {
      consumer_cod_id: form.consumer_cod_id,
      consumer_id: form.consumer_id,
      cognome: form.cognome,
      nome: form.nome,
      indirizzo: emptyToNull(form.indirizzo),
      citta: emptyToNull(form.citta),
      pr: emptyToNull(form.pr),
      nazione: emptyToNull(form.nazione),
      cap: emptyToNull(form.cap),
      telefono: emptyToNull(form.telefono) ?? '',
      telefono2: emptyToNull(form.telefono2) ?? '',
      eta: form.eta ? Number(form.eta) : null,
      note: emptyToNull(form.note) ?? '',
      mz: form.mz,                // "Si" | "No"
      fidelity: form.fidelity,    // 0 | 1
      data_creaz_negozio: form.data_creaz_negozio || null,
      data_creaz_assoluto: form.data_creaz_assoluto || null,
      negozio: emptyToNull(form.negozio),
      email: emptyToNull(form.email) ?? '',
      titolo: emptyToNull(form.titolo) ?? '',
      nc: form.nc ?? '',
      promozione: emptyToNull(form.promozione) ?? '',
      data_nascita: form.data_nascita || null,
      ult_acq_negozio: form.ult_acq_negozio || null,
      ult_acq_assoluto: form.ult_acq_assoluto || null,
      pref_contatto: emptyToNull(form.pref_contatto) ?? '',
      professione: emptyToNull(form.professione) ?? '',
      interessi: emptyToNull(form.interessi) ?? '',
      building: emptyToNull(form.building) ?? '',
      apt_number: emptyToNull(form.apt_number) ?? '',
    };

    try {
      setSaving(true);
      await putJson(`/anagrafica/edit`, body, { params: { ID: id } });
      toast.success('Anagrafica aggiornata');
    } catch (e: any) {
      const msg = e?.response?.data?.message ?? e?.message ?? 'Errore nel salvataggio';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="page-body pt-4">
        <Col xl={12} className="mb-lg-3 d-flex justify-content-end">
          <Link to="/report/anagrafe">
            <Btn className="px-4" color="primary">Torna Indietro</Btn>
          </Link>
        </Col>
        <Col xl={12}>
          <Card><CardBody className="d-flex justify-content-center py-5"><Spinner /></CardBody></Card>
        </Col>
      </div>
    );
  }

  return (
    <div className="page-body pt-4">



      <Row className="mb-3">
        <Col xl={12} >
          <Card>
            <CardBody className="">
              <Row>
                <Col md={12}>
                  <H3>Modifica Profilo</H3>
                </Col>
              </Row>
              <Row className='mt-3'>
                <Col className='w-100 d-flex justify-content-between'>
                  <div className='gap-3 flex-column flex-md-row d-none d-md-flex'>
                    <Btn
                      style={{ width: 140 }}
                      type="button"
                      color="secondary"
                      className="border"
                      onClick={() => scrollToSection(profiloRef)}
                    >
                      Dati Profilo
                    </Btn>
                    <Btn
                      style={{ width: 140 }}
                      type="button"
                      color="secondary"
                      className="border"
                      onClick={() => scrollToSection(indirizzoRef)}
                    >
                      Dati Indirizzo
                    </Btn>
                    <Btn
                      style={{ width: 140 }}
                      type="button"
                      color="secondary"
                      className="border"
                      onClick={() => scrollToSection(negozioRef)}
                    >
                      Dati Negozio
                    </Btn>
                    <Btn
                      style={{ width: 140 }}
                      type="button"
                      color="secondary"
                      className="border"
                      onClick={() => scrollToSection(marketingRef)}
                    >
                      Dati Marketing
                    </Btn>
                    <Btn
                      style={{ width: 140 }}
                      type="button"
                      color="secondary"
                      className="border"
                      onClick={() => scrollToSection(noteRef)}
                    >
                      Note
                    </Btn>
                  </div>

                  <div>
                    <Link to="/report/anagrafe">
                      <Btn className="px-4" color="primary">Torna Indietro</Btn>
                    </Link>
                  </div>
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Col>
      </Row>

      <Form onSubmit={onSubmit}>
        <Row>
          <Col xl={4} className="mb-lg-3 m-0">
            <Card style={{ borderRadius: '1rem' }} className="flex-fill h-100">
              <div ref={profiloRef}>
                <CardBody>
                  <H3 className="mb-3">Dati Profilo</H3>
                  <Row className='d-flex flex-column gap-3'>
                    <Col lg={12}>
                      <Label>Consumer Cod ID</Label>
                      <Input readOnly value={form.consumer_cod_id} />
                    </Col>
                    <Col lg={12}>
                      <Label>Nome</Label>
                      <Input name="nome" value={form.nome} onChange={onChange} />
                    </Col>
                    <Col lg={12}>
                      <Label>Cognome</Label>
                      <Input name="cognome" value={form.cognome} onChange={onChange} />
                    </Col>
                    <Col lg={12}>
                      <Label>Età</Label>
                      <Input type="number" name="eta" value={form.eta} onChange={onChange} placeholder="Età" />
                    </Col>
                    <Col lg={12}>
                      <Label>Data di Nascita</Label>
                      <Input type="date" name="data_nascita" value={form.data_nascita} onChange={onChange} />
                    </Col>

                    <Col lg={12}>
                      <Label>Telefono</Label>
                      <Input name="telefono" value={form.telefono} onChange={onChange} />
                    </Col>
                    <Col lg={12}>
                      <Label>Telefono 2</Label>
                      <Input name="telefono2" value={form.telefono2} onChange={onChange} />
                    </Col>
                    <Col lg={12}>
                      <Label>Email</Label>
                      <Input type="email" name="email" value={form.email} onChange={onChange} />
                    </Col>
                  </Row>
                </CardBody>
              </div>
            </Card>

          </Col>

          <Col xl={8} className="mb-lg-3 m-0">
            <Card style={{ borderRadius: '1rem' }} className="flex-fill h-100">
              <div ref={indirizzoRef}>
                <CardBody>
                  <H3 className="mb-3">Dati Indirizzo</H3>
                  <Row className='d-flex gy-3'>
                    <Col lg={8}>
                      <Label>Indirizzo</Label>
                      <Input name="indirizzo" value={form.indirizzo} onChange={onChange} />
                    </Col>
                    <Col lg={4}>
                      <Label>CAP</Label>
                      <Input name="cap" value={form.cap} onChange={onChange} />
                    </Col>

                    <Col lg={6}>
                      <Label>Città</Label>
                      <Input name="citta" value={form.citta} onChange={onChange} />
                    </Col>
                    <Col lg={6}>
                      <Label>Nazione</Label>
                      <Input name="nazione" value={form.nazione} onChange={onChange} />
                    </Col>
                    <Col lg={3}>
                      <Label>PR</Label>
                      <Input name="pr" value={form.pr} onChange={onChange} />
                    </Col>
                    <Col lg={6}>
                      <Label>Apt/Interno</Label>
                      <Input name="apt_number" value={form.apt_number} onChange={onChange} />
                    </Col>
                    <Col lg={3}>
                      <Label>Building</Label>
                      <Input name="building" value={form.building} onChange={onChange} />
                    </Col>
                  </Row>
                </CardBody>
              </div>
            </Card>

          </Col>

          <Col xl={12} className="mb-lg-3 m-0">
            <div ref={negozioRef} >
              <Card style={{ borderRadius: '1rem' }} className="flex-fill h-100">
                <CardBody>
                  <H3 className="mb-3">Dati Negozio</H3>
                  <Row className='d-flex gy-3'>
                    <Col lg={6}>
                      <Label>Negozio</Label>
                      <Input name="negozio" value={form.negozio} onChange={onChange} />
                    </Col>
                    <Col lg={6}>
                      <Label>PR</Label>
                      <Input name="pr" value={form.pr} onChange={onChange} />
                    </Col>

                    <Col lg={12}>
                      <Label>Titolo</Label>
                      <Input name="titolo" value={form.titolo} onChange={onChange} />
                    </Col>
                    <Col lg={3}>
                      <Label>Fidelity</Label>
                      <Input type="select" name="fidelity" value={form.fidelity} onChange={(e) => setForm(p => ({ ...p, fidelity: Number(e.target.value) as 0 | 1 }))}>
                        <option value={0}>No</option>
                        <option value={1}>Si</option>
                      </Input>
                    </Col>
                    <Col lg={3}>
                      <Label>NC</Label>
                      <Input name="nc" value={form.nc} onChange={onChange} />
                    </Col>
                    <Col lg={3}>
                      <Label>Promozione</Label>
                      <Input name="promozione" value={form.promozione} onChange={onChange} />
                    </Col>
                    <Col lg={3}>
                      <Label>MZ</Label>
                      <Input type="select" name="mz" value={form.mz} onChange={(e) => setForm(p => ({ ...p, mz: e.target.value as 'Si' | 'No' }))}>
                        <option value="No">No</option>
                        <option value="Si">Si</option>
                      </Input>
                    </Col>
                    <Col lg={3}>
                      <Label>Data Creazione (Negozio)</Label>
                      <Input type="date" name="data_creaz_negozio" value={form.data_creaz_negozio} onChange={onChange} />
                    </Col>
                    <Col lg={3}>
                      <Label>Data Creazione (Assoluto)</Label>
                      <Input type="date" name="data_creaz_assoluto" value={form.data_creaz_assoluto} onChange={onChange} />
                    </Col>
                    <Col lg={3}>
                      <Label>Ultimo Acquisto (Negozio)</Label>
                      <Input type="date" name="ult_acq_negozio" value={form.ult_acq_negozio} onChange={onChange} />
                    </Col>
                    <Col lg={3}>
                      <Label>Ultimo Acquisto (Assoluto)</Label>
                      <Input type="date" name="ult_acq_assoluto" value={form.ult_acq_assoluto} onChange={onChange} />
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </div>
          </Col>

          <Col xl={12} className="mb-lg-3 m-0">
            <div ref={marketingRef}>
              <Card style={{ borderRadius: '1rem' }} className="flex-fill h-100">
                <CardBody>
                  <H3 className="mb-3">Dati Marketing</H3>
                  <Row className='d-flex gy-3'>
                    <Col lg={6}>
                      <Label>Preferenza Contatto</Label>
                      <Input name="pref_contatto" value={form.pref_contatto} onChange={onChange} />
                    </Col>
                    <Col lg={6}>
                      <Label>Professione</Label>
                      <Input name="professione" value={form.professione} onChange={onChange} />
                    </Col>

                    <Col lg={12}>
                      <Label>Interessi</Label>
                      <Input name="interessi" value={form.interessi} onChange={onChange} />
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </div>
          </Col>
        </Row>

        <Col xl={12}>
          <Card>
            <CardBody>
              <Row className="g-3">
                <Col xs={12} className="mt-2 d-flex justify-content-between align-items-center gap-2">
                  <H3>Modifica Profilo</H3>
                  <div className='d-flex gap-3'>
                    <Btn color="primary" className="px-4" type="submit" disabled={saving}>Aggiorna</Btn>
                    {saving && <Spinner size="sm" />}
                  </div>
                </Col>
              </Row>
            </CardBody>
          </Card>

          <div ref={noteRef}>
            <NotesSection consumerId={form.consumer_id} />
          </div>
        </Col>
      </Form>
    </div >
  );
}

function emptyToNull(v: string) {
  return v?.trim() === '' ? null : v;
}
